/**
 * ALTIORA · Knowledge Ingestion Center — page d'administration.
 *
 * Permet d'ingérer massivement des connaissances depuis :
 *  - notes / emails / transcriptions (texte)
 *  - URL / RSS (téléchargement)
 *  - fichiers CSV / Excel / PDF / Word (upload)
 *
 * Chaque job produit des propositions à valider avant publication.
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Upload, Link as LinkIcon, FileText, Radio, Wand2, Check, X,
  ExternalLink, Copy, ChevronDown, ChevronRight,
} from "lucide-react";
import {
  PageShell, PageHeader, Card, PrimaryButton, GhostButton, FormField,
  Input, Alert, Loader, NAVY, CHAMPAGNE, SLATE, NAVY_DARK as INK,
} from "@/components/altiora/PremiumUI";
import { TopBar } from "@/components/layout/TopBar";
import { api } from "@/lib/api";

const KIND_LABELS = {
  csv: "CSV", excel: "Excel (xlsx)", pdf: "PDF", word: "Word (docx)",
  url: "URL / page web", rss: "Flux RSS", api: "API JSON",
  email: "Email", note: "Note libre",
  audio_transcript: "Transcript audio", video_transcript: "Transcript vidéo",
};

const STATUS_COLORS = {
  queued: "#6b7c93", downloading: "#c9a34e", analyzing: "#c9a34e",
  proposed: "#0a2540", approved: "#0a7f42", rejected: "#8B4E00", failed: "#c95050",
};

export default function AdminKnowledgeIngestion() {
  const [jobs, setJobs] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedJob, setSelectedJob] = useState(null);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [j, s] = await Promise.all([
        api.get("/knowledge-ingestion/jobs?limit=50"),
        api.get("/knowledge-ingestion/stats"),
      ]);
      setJobs(j.data.jobs || []);
      setStats(s.data);
    } catch { toast.error("Chargement impossible"); }
    finally { setLoading(false); }
  };

  const openJob = async (id) => {
    try {
      const { data } = await api.get(`/knowledge-ingestion/jobs/${id}`);
      setSelectedJob(data);
    } catch { toast.error("Job introuvable"); }
  };

  useEffect(() => { loadAll(); }, []);

  return (
    <PageShell>
      <TopBar />
      <div className="max-w-[1400px] mx-auto px-6 py-8" data-testid="admin-kic-root">
        <PageHeader
          eyebrow="ALTIORA · Administration"
          title="Knowledge Ingestion Center"
          subtitle="Nourrissez le Knowledge Engine avec du contenu multi-format. Chaque proposition est validée avant publication."
          testid="admin-kic-header"
        />

        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-6" data-testid="admin-kic-stats">
            <StatTile label="Total jobs" value={stats.total_jobs} />
            <StatTile label="Propositions ouvertes"
                       value={stats.by_status?.proposed || 0} color={STATUS_COLORS.proposed} />
            <StatTile label="Approuvés"
                       value={stats.by_status?.approved || 0} color={STATUS_COLORS.approved} />
            <StatTile label="Échecs"
                       value={stats.by_status?.failed || 0} color={STATUS_COLORS.failed} />
          </div>
        )}

        {/* Zones d'ingestion (4 · texte, URL, fichier, réunion) */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mt-6">
          <TextIngestForm onDone={loadAll} />
          <UrlIngestForm onDone={loadAll} />
          <FileIngestForm onDone={loadAll} />
          <MeetingIngestForm onDone={loadAll} />
        </div>

        {/* Historique des jobs */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-6">
          <div className="lg:col-span-1">
            <Card className="p-0" testid="admin-kic-jobs-card">
              <div className="p-4 border-b" style={{ borderColor: "#e5e0d0" }}>
                <div className="text-[11px] uppercase tracking-widest font-mono"
                     style={{ color: SLATE }}>
                  Historique des jobs ({jobs.length})
                </div>
              </div>
              {loading ? (
                <div className="p-8 flex justify-center"><Loader size={18} /></div>
              ) : (
                <ul className="max-h-[600px] overflow-y-auto" data-testid="admin-kic-jobs">
                  {jobs.length === 0 ? (
                    <li className="p-4 text-center text-xs" style={{ color: SLATE }}>
                      Aucun job pour l'instant.
                    </li>
                  ) : jobs.map((j) => (
                    <li key={j.id}
                        onClick={() => openJob(j.id)}
                        className={`p-3 border-b cursor-pointer transition ${selectedJob?.id === j.id ? "bg-amber-50" : "hover:bg-gray-50"}`}
                        style={{ borderColor: "#e5e0d0" }}
                        data-testid={`admin-kic-job-${j.id}`}>
                      <div className="text-sm font-medium truncate" style={{ color: NAVY }}>
                        {j.source_label}
                      </div>
                      <div className="text-[10px] mt-1 flex items-center gap-2"
                           style={{ color: SLATE }}>
                        <span className="uppercase font-mono"
                              style={{ color: STATUS_COLORS[j.status] || SLATE }}>
                          {j.status}
                        </span>
                        <span>· {KIND_LABELS[j.kind] || j.kind}</span>
                        <span>· {j.proposals_count || 0} prop.</span>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </Card>
          </div>
          <div className="lg:col-span-2">
            {selectedJob ? (
              <JobDetailPanel job={selectedJob} onRefresh={() => openJob(selectedJob.id)}
                                onListRefresh={loadAll} />
            ) : (
              <Card className="p-8 text-center" testid="admin-kic-empty">
                <Wand2 size={26} className="mx-auto mb-2" style={{ color: CHAMPAGNE }} />
                <div className="text-sm" style={{ color: NAVY }}>
                  Créez un job (ci-dessus) ou cliquez sur un job pour voir les propositions.
                </div>
              </Card>
            )}
          </div>
        </div>

        {/* Automatic Sources Watcher */}
        <div className="mt-6">
          <SourcesWatcherPanel onNewJobs={loadAll} />
        </div>
      </div>
    </PageShell>
  );
}

function StatTile({ label, value, color }) {
  return (
    <Card className="p-3">
      <div className="text-[10px] uppercase tracking-widest font-mono"
           style={{ color: SLATE }}>{label}</div>
      <div className="text-2xl font-serif mt-1"
           style={{ color: color || NAVY }}>
        {value?.toLocaleString?.("fr-CA") ?? value}
      </div>
    </Card>
  );
}

/* ── Automatic Sources Watcher ────────────────────────────────────── */

function SourcesWatcherPanel({ onNewJobs }) {
  const [sources, setSources] = useState([]);
  const [busy, setBusy] = useState(false);
  const [running, setRunning] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/knowledge-ingestion/watcher/sources");
      setSources(data.sources || []);
    } catch { toast.error("Watcher indisponible"); }
  };
  useEffect(() => { load(); }, []);

  const toggle = async (id, active) => {
    setBusy(true);
    try {
      await api.post(`/knowledge-ingestion/watcher/sources/${id}/toggle?active=${active}`);
      await load();
    } catch { toast.error("Impossible de changer l'état"); }
    finally { setBusy(false); }
  };

  const runNow = async () => {
    setRunning(true);
    try {
      const { data } = await api.post("/knowledge-ingestion/watcher/run");
      toast.success(`Watcher exécuté — ${data.changed} changement(s) détecté(s), ${data.jobs_created.length} job(s) créé(s)`);
      await load();
      onNewJobs?.();
    } catch { toast.error("Exécution watcher échouée"); }
    finally { setRunning(false); }
  };

  return (
    <Card className="p-5" testid="admin-kic-watcher-panel">
      <div className="flex justify-between items-start mb-3">
        <div className="flex items-center gap-2">
          <Radio size={16} style={{ color: NAVY }} />
          <div>
            <h3 className="font-serif text-base" style={{ color: NAVY }}>
              Automatic Sources Watcher
            </h3>
            <div className="text-[11px]" style={{ color: SLATE }}>
              Surveille quotidiennement les sources officielles (04:15 UTC) et crée des jobs KIC
              lorsque de nouveaux contenus sont détectés.
            </div>
          </div>
        </div>
        <PrimaryButton onClick={runNow} disabled={running}
                        testid="admin-kic-watcher-run">
          {running ? "Exécution…" : "Lancer maintenant"}
        </PrimaryButton>
      </div>
      <ul className="mt-2 space-y-1" data-testid="admin-kic-watcher-sources">
        {sources.map((s) => (
          <li key={s.id} className="p-2 rounded flex justify-between items-center"
              style={{ background: "#faf6ea", border: "1px solid #e5d9b8" }}
              data-testid={`admin-kic-watcher-source-${s.id}`}>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium truncate" style={{ color: NAVY }}>
                {s.label}
              </div>
              <a href={s.url} target="_blank" rel="noreferrer"
                  className="text-[10px] hover:underline truncate block"
                  style={{ color: CHAMPAGNE }}>{s.url}</a>
              <div className="text-[10px]" style={{ color: SLATE }}>
                {s.last_checked_at
                  ? `Dernière vérif : ${new Date(s.last_checked_at).toLocaleString("fr-CA")}`
                  : "Jamais vérifié"}
                {s.last_job_id && ` · dernier job ${s.last_job_id.slice(-8)}`}
              </div>
            </div>
            <button
              onClick={() => toggle(s.id, !s.active)}
              disabled={busy}
              className="text-[11px] px-2 py-1 rounded ml-3"
              style={{
                background: s.active ? "#0a7f42" : "#eee",
                color: s.active ? "#fff" : NAVY,
              }}
              data-testid={`admin-kic-watcher-toggle-${s.id}`}
            >
              {s.active ? "Actif" : "Inactif"}
            </button>
          </li>
        ))}
      </ul>
    </Card>
  );
}

/* ── Formulaires d'ingestion ──────────────────────────────────────── */

function TextIngestForm({ onDone }) {
  const [kind, setKind] = useState("note");
  const [label, setLabel] = useState("");
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!label.trim() || !text.trim()) return toast.error("Titre et contenu requis");
    setBusy(true);
    try {
      await api.post("/knowledge-ingestion/jobs/text", {
        kind, source_label: label, payload_text: text,
      });
      toast.success("Job créé et analysé");
      setLabel(""); setText(""); onDone();
    } catch { toast.error("Ingestion échouée"); }
    finally { setBusy(false); }
  };
  return (
    <Card className="p-4" testid="admin-kic-form-text">
      <div className="flex items-center gap-2 mb-2">
        <FileText size={14} style={{ color: NAVY }} />
        <div className="text-sm font-serif" style={{ color: NAVY }}>
          Ingérer un texte
        </div>
      </div>
      <FormField label="Type" testid="admin-kic-ff-kind">
        {() => (
          <select data-testid="admin-kic-text-kind"
                  className="w-full border rounded px-2 py-1 text-sm"
                  value={kind} onChange={(e) => setKind(e.target.value)}>
            {["note", "email", "audio_transcript", "video_transcript",
              "pdf", "word", "excel", "api"].map((k) => (
              <option key={k} value={k}>{KIND_LABELS[k]}</option>
            ))}
          </select>
        )}
      </FormField>
      <FormField label="Nom de la source" testid="admin-kic-ff-label">
        {(a) => <Input testid="admin-kic-text-label" value={label}
                       onChange={(e) => setLabel(e.target.value)} {...a} />}
      </FormField>
      <FormField label="Contenu (paragraphes séparés par ligne vide)"
                  testid="admin-kic-ff-text">
        {() => (
          <textarea data-testid="admin-kic-text-body" rows={5}
                     className="w-full border rounded px-2 py-1 text-xs font-mono"
                     value={text} onChange={(e) => setText(e.target.value)} />
        )}
      </FormField>
      <div className="mt-2">
        <PrimaryButton onClick={submit} disabled={busy} testid="admin-kic-text-submit">
          <Wand2 size={12} /> {busy ? "Analyse…" : "Ingérer et analyser"}
        </PrimaryButton>
      </div>
    </Card>
  );
}

function UrlIngestForm({ onDone }) {
  const [url, setUrl] = useState("");
  const [kind, setKind] = useState("url");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!url.trim()) return toast.error("URL requise");
    setBusy(true);
    try {
      await api.post("/knowledge-ingestion/jobs/url", { url, kind });
      toast.success("URL analysée");
      setUrl(""); onDone();
    } catch { toast.error("Ingestion URL échouée"); }
    finally { setBusy(false); }
  };
  return (
    <Card className="p-4" testid="admin-kic-form-url">
      <div className="flex items-center gap-2 mb-2">
        <LinkIcon size={14} style={{ color: NAVY }} />
        <div className="text-sm font-serif" style={{ color: NAVY }}>
          Ingérer une URL / RSS
        </div>
      </div>
      <FormField label="Type" testid="admin-kic-ff-url-kind">
        {() => (
          <select data-testid="admin-kic-url-kind"
                  className="w-full border rounded px-2 py-1 text-sm"
                  value={kind} onChange={(e) => setKind(e.target.value)}>
            <option value="url">URL (page)</option>
            <option value="rss">Flux RSS</option>
          </select>
        )}
      </FormField>
      <FormField label="URL" testid="admin-kic-ff-url">
        {(a) => <Input testid="admin-kic-url-input" value={url}
                       onChange={(e) => setUrl(e.target.value)}
                       placeholder="https://…" {...a} />}
      </FormField>
      <div className="mt-2">
        <PrimaryButton onClick={submit} disabled={busy} testid="admin-kic-url-submit">
          <Radio size={12} /> {busy ? "Téléchargement…" : "Analyser"}
        </PrimaryButton>
      </div>
    </Card>
  );
}

function FileIngestForm({ onDone }) {
  const [file, setFile] = useState(null);
  const [kind, setKind] = useState("csv");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!file) return toast.error("Fichier requis");
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      await api.post(`/knowledge-ingestion/jobs/file?kind=${kind}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Fichier ingéré");
      setFile(null); onDone();
    } catch { toast.error("Ingestion fichier échouée"); }
    finally { setBusy(false); }
  };
  return (
    <Card className="p-4" testid="admin-kic-form-file">
      <div className="flex items-center gap-2 mb-2">
        <Upload size={14} style={{ color: NAVY }} />
        <div className="text-sm font-serif" style={{ color: NAVY }}>
          Ingérer un fichier
        </div>
      </div>
      <FormField label="Type" testid="admin-kic-ff-file-kind">
        {() => (
          <select data-testid="admin-kic-file-kind"
                  className="w-full border rounded px-2 py-1 text-sm"
                  value={kind} onChange={(e) => setKind(e.target.value)}>
            {["csv", "excel", "pdf", "word"].map((k) => (
              <option key={k} value={k}>{KIND_LABELS[k]}</option>
            ))}
          </select>
        )}
      </FormField>
      <div className="mt-2 mb-3">
        <input type="file" accept=".csv,.xlsx,.pdf,.docx,.txt"
               data-testid="admin-kic-file-input"
               onChange={(e) => setFile(e.target.files?.[0] || null)} />
      </div>
      <PrimaryButton onClick={submit} disabled={busy || !file}
                      testid="admin-kic-file-submit">
        <Upload size={12} /> {busy ? "Analyse…" : "Ingérer"}
      </PrimaryButton>
    </Card>
  );
}


function MeetingIngestForm({ onDone }) {
  const [url, setUrl] = useState("");
  const [transcript, setTranscript] = useState("");
  const [label, setLabel] = useState("");
  const [participants, setParticipants] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!url.trim() && !transcript.trim()) {
      return toast.error("URL ou transcript requis");
    }
    setBusy(true);
    try {
      await api.post("/knowledge-ingestion/jobs/meeting", {
        meeting_url: url.trim() || null,
        transcript_text: transcript.trim() || null,
        source_label: label.trim() || null,
        participants: participants.split(",").map(s => s.trim()).filter(Boolean),
      });
      toast.success("Réunion ingérée");
      setUrl(""); setTranscript(""); setLabel(""); setParticipants("");
      onDone();
    } catch { toast.error("Ingestion réunion échouée"); }
    finally { setBusy(false); }
  };
  return (
    <Card className="p-4" testid="admin-kic-form-meeting">
      <div className="flex items-center gap-2 mb-2">
        <Radio size={14} style={{ color: NAVY }} />
        <div className="text-sm font-serif" style={{ color: NAVY }}>
          Ingérer une réunion
        </div>
      </div>
      <FormField label="URL Zoom / Teams / Meet / YouTube (optionnel)"
                    testid="admin-kic-ff-meet-url">
        {() => <Input testid="admin-kic-meet-url" value={url}
                        onChange={(e) => setUrl(e.target.value)}
                        placeholder="https://zoom.us/rec/…" />}
      </FormField>
      <FormField label="Ou transcript collé" testid="admin-kic-ff-meet-transcript">
        {() => <textarea data-testid="admin-kic-meet-transcript"
                            value={transcript} rows={3}
                            onChange={(e) => setTranscript(e.target.value)}
                            className="w-full border rounded px-2 py-1 text-sm"
                            placeholder="Coller ici les notes ou le transcript…" />}
      </FormField>
      <FormField label="Titre (optionnel)" testid="admin-kic-ff-meet-label">
        {() => <Input testid="admin-kic-meet-label" value={label}
                        onChange={(e) => setLabel(e.target.value)}
                        placeholder="Comité stratégique · 2026-08-07" />}
      </FormField>
      <FormField label="Participants (séparés par virgule)"
                    testid="admin-kic-ff-meet-participants">
        {() => <Input testid="admin-kic-meet-participants" value={participants}
                        onChange={(e) => setParticipants(e.target.value)}
                        placeholder="Marc L., Sophie B." />}
      </FormField>
      <PrimaryButton onClick={submit} disabled={busy}
                        testid="admin-kic-meet-submit">
        <Radio size={12} /> {busy ? "Analyse…" : "Ingérer réunion"}
      </PrimaryButton>
    </Card>
  );
}


/* ── Panneau détail d'un job ──────────────────────────────────────── */

function JobDetailPanel({ job, onRefresh, onListRefresh }) {
  return (
    <Card className="p-5" testid="admin-kic-detail">
      <div className="flex justify-between items-start">
        <div>
          <div className="text-[10px] uppercase tracking-widest font-mono"
               style={{ color: STATUS_COLORS[job.status] || SLATE }}>
            {job.status} · {KIND_LABELS[job.kind]}
          </div>
          <h3 className="font-serif text-lg mt-1" style={{ color: NAVY }}>
            {job.source_label}
          </h3>
          {job.source_ref && (
            <a href={job.source_ref} target="_blank" rel="noreferrer"
                className="text-xs flex items-center gap-1 mt-1 hover:underline"
                style={{ color: CHAMPAGNE }}>
              <ExternalLink size={10} /> {job.source_ref}
            </a>
          )}
        </div>
        {job.stats && (
          <div className="text-[10px] text-right font-mono uppercase tracking-widest"
               style={{ color: SLATE }}>
            {job.stats.rows_read} lus · {job.stats.duplicates_flagged || 0} doublons
            {job.stats.published ? ` · ${job.stats.published} publiés` : ""}
          </div>
        )}
      </div>

      {job.error && (
        <Alert variant="warning" title="Erreur d'analyse" testid="admin-kic-error">
          {job.error}
        </Alert>
      )}

      <ul className="mt-4 space-y-2" data-testid="admin-kic-proposals">
        {(job.proposals || []).length === 0 && (
          <li className="text-xs text-center" style={{ color: SLATE }}>
            Aucune proposition extraite.
          </li>
        )}
        {(job.proposals || []).map((p) => (
          <ProposalCard key={p.id} proposal={p}
                         onApprove={async () => {
                           try {
                             await api.post(
                               `/knowledge-ingestion/jobs/${job.id}/proposals/${p.id}/approve`,
                               {},
                             );
                             toast.success("Proposition publiée dans le Knowledge Engine");
                             onRefresh(); onListRefresh();
                           } catch { toast.error("Publication impossible"); }
                         }}
                         onReject={async () => {
                           try {
                             await api.post(
                               `/knowledge-ingestion/jobs/${job.id}/proposals/${p.id}/reject`,
                               {},
                             );
                             toast.success("Rejetée"); onRefresh();
                           } catch { toast.error("Rejet impossible"); }
                         }} />
        ))}
      </ul>
    </Card>
  );
}

function ProposalCard({ proposal, onApprove, onReject }) {
  const [open, setOpen] = useState(false);
  const isDup = !!proposal.is_duplicate_of;
  const isDone = proposal.approved || proposal.notes?.startsWith("Rejeté");
  return (
    <li className="border rounded p-3"
        style={{ borderColor: isDup ? "#f3b8b8" : "#e5d9b8",
                  background: isDone ? "#f0f5ef" : (isDup ? "#fdecec" : "#faf6ea") }}
        data-testid={`admin-kic-prop-${proposal.id}`}>
      <div className="flex justify-between items-start gap-3">
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium" style={{ color: NAVY }}>
            {proposal.title}
          </div>
          {proposal.summary && (
            <div className="text-xs mt-0.5" style={{ color: INK }}>
              {proposal.summary}
            </div>
          )}
          <div className="text-[10px] mt-1 flex flex-wrap gap-2"
               style={{ color: SLATE }}>
            <span className="uppercase font-mono">
              {proposal.source_type}
            </span>
            <span>Confiance {proposal.confidence}%</span>
            {isDup && (
              <span className="flex items-center gap-1"
                    style={{ color: "#c95050" }}>
                <Copy size={10} /> Doublon potentiel de {proposal.is_duplicate_of}
                {" "} (score {proposal.duplicate_score})
              </span>
            )}
            {proposal.approved && (
              <span style={{ color: "#0a7f42" }}>✓ publié → {proposal.published_item_id}</span>
            )}
            {proposal.notes?.startsWith("Rejeté") && (
              <span style={{ color: "#8B4E00" }}>Rejeté</span>
            )}
          </div>
        </div>
        {!isDone && (
          <div className="flex gap-1">
            <button onClick={onApprove}
                     className="text-[11px] px-2 py-1 rounded"
                     style={{ background: "#0a7f42", color: "#fff" }}
                     data-testid={`admin-kic-approve-${proposal.id}`}>
              <Check size={11} className="inline" /> Publier
            </button>
            <button onClick={onReject}
                     className="text-[11px] px-2 py-1 rounded"
                     style={{ background: "#eee", color: "#7a1e1e" }}
                     data-testid={`admin-kic-reject-${proposal.id}`}>
              <X size={11} className="inline" /> Rejeter
            </button>
          </div>
        )}
      </div>
      {proposal.body_md && (
        <div className="mt-2">
          <button onClick={() => setOpen((o) => !o)}
                   className="text-[10px] flex items-center gap-1"
                   style={{ color: CHAMPAGNE }}
                   data-testid={`admin-kic-toggle-${proposal.id}`}>
            {open ? <ChevronDown size={10} /> : <ChevronRight size={10} />}
            Voir le contenu extrait
          </button>
          {open && (
            <pre className="text-[10px] mt-1 p-2 rounded whitespace-pre-wrap"
                 style={{ background: "#fff", border: "1px solid #e5d9b8",
                          color: INK, maxHeight: 300, overflow: "auto" }}>
{proposal.body_md}
            </pre>
          )}
        </div>
      )}
    </li>
  );
}
